import React from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Typography } from '@mui/material'

export type HierarchyLevel = 'category' | 'type' | 'blueprintFamily' | 'blueprint'

// What each level means, on hover over its label. One definition per level, so
// every screen that names a level (Live Event Analytics filters, Data Glossary
// filters, the Live Events Tracker feed band and the event dialog path)
// explains it identically.
export const HIERARCHY_TOOLTIP_KEYS: Record<HierarchyLevel, { title: string; description: string; level?: string; example?: string }> = {
  category: {
    title: 'live-events:hierarchy_tooltip_event_category_title',
    description: 'live-events:hierarchy_tooltip_event_category_description',
    level: 'live-events:hierarchy_tooltip_event_category_level',
    example: 'live-events:hierarchy_tooltip_event_category_example',
  },
  type: {
    title: 'live-events:hierarchy_tooltip_event_type_title',
    description: 'live-events:hierarchy_tooltip_event_type_description',
    level: 'live-events:hierarchy_tooltip_event_type_level',
    example: 'live-events:hierarchy_tooltip_event_type_example',
  },
  blueprintFamily: {
    title: 'live-events:hierarchy_tooltip_blueprint_family_title',
    description: 'live-events:hierarchy_tooltip_blueprint_family_description',
    level: 'live-events:hierarchy_tooltip_blueprint_family_level',
    example: 'live-events:hierarchy_tooltip_blueprint_family_example',
  },
  blueprint: { title: 'live-events:hierarchy_tooltip_blueprint_title', description: 'live-events:hierarchy_tooltip_blueprint_description' },
}

export const HIERARCHY_EXAMPLE_LABEL_KEY = 'live-events:hierarchy_tooltip_example_label'

// An example path is one string with its segments parted by this separator, so translators see the whole path.
export const EXAMPLE_PATH_SEPARATOR = ' › '

// A label ending in CJK punctuation ("例：", "例如：") already carries its own spacing, so no space is added before the
// example text. Covers CJK symbols and punctuation (、。」】), full-width punctuation forms and brackets, but not
// full-width letters or digits or half-width katakana.
export const ENDS_WITH_CJK_PUNCTUATION = /[\u3000-\u303F\uFF01-\uFF0F\uFF1A-\uFF20\uFF3B-\uFF40\uFF5B-\uFF65]$/

interface EventLevelTooltipContentProps {
  level: HierarchyLevel
  // Surfaces whose row label already names the level (the filter rows) pass false
  // to show the definition alone, like the Game Category rows next to them.
  showTitle?: boolean
  // Adds the level subtitle and the example path from the level's own copy ("Broadest level", "e.g. Monetization").
  // The blueprint level has neither, so it renders the same with or without this. `levelLabel`, `example` and
  // `exampleLabel` override the copy when passed.
  showLevelDetails?: boolean
  // Already-translated italic, muted subtitle between the title and the definition (e.g. "Broadest level").
  levelLabel?: string
  // Already-translated example path, segments parted by EXAMPLE_PATH_SEPARATOR; only the last segment is bold.
  example?: string
  // Already-translated lead-in for the example line (e.g. "e.g."). The example line renders only when both `example` and `exampleLabel` are set.
  exampleLabel?: string
}

/**
 * Tooltip body for a taxonomy level, as a stack with the same rhythm on every screen: a bold level name
 * (optional), an optional italic level subtitle, then the definition and its optional example path
 * ("e.g. A › B › **C**") kept together as one block. The title and the subtitle each get a theme spacing
 * step below them; the definition and example sit flush.
 */
export const EventLevelTooltipContent: React.FC<EventLevelTooltipContentProps> = ({
  level,
  showTitle = true,
  showLevelDetails = false,
  levelLabel: levelLabelProp,
  example: exampleProp,
  exampleLabel: exampleLabelProp,
}) => {
  const { t } = useTranslation()
  const keys = HIERARCHY_TOOLTIP_KEYS[level]
  const levelLabel = levelLabelProp ?? (showLevelDetails && keys.level ? t(keys.level) : undefined)
  const example = exampleProp ?? (showLevelDetails && keys.example ? t(keys.example) : undefined)
  const exampleLabel = exampleLabelProp ?? (showLevelDetails && keys.example ? t(HIERARCHY_EXAMPLE_LABEL_KEY) : undefined)
  const exampleSegments = example && exampleLabel ? example.split(EXAMPLE_PATH_SEPARATOR) : []
  const exampleSeparator = exampleLabel && !ENDS_WITH_CJK_PUNCTUATION.test(exampleLabel) ? ' ' : ''

  return (
    <>
      {showTitle && (
        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t(keys.title)}
        </Typography>
      )}
      {levelLabel && (
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 1 }}>
          {levelLabel}
        </Typography>
      )}
      <Box>
        <Typography variant="body2">{t(keys.description)}</Typography>
        {exampleSegments.length > 0 && (
          <Typography variant="body2">
            {exampleLabel}
            {exampleSeparator}
            {exampleSegments.map((segment, index) => {
              const isLast = index === exampleSegments.length - 1
              return (
                <Box component="span" key={`${index}-${segment}`}>
                  {isLast ? <strong>{segment}</strong> : segment}
                  {!isLast && EXAMPLE_PATH_SEPARATOR}
                </Box>
              )
            })}
          </Typography>
        )}
      </Box>
    </>
  )
}
